import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Box,
  Slider,
  TextField,
  Paper,
} from '@mui/material';

function Calculator() {
  const [loanAmount, setLoanAmount] = useState(2500000);
  const [tenure, setTenure] = useState(20);
  const [interestRate, setInterestRate] = useState(8.5);
  const [emi, setEmi] = useState(0);
  const [totalInterest, setTotalInterest] = useState(0);
  const [totalAmount, setTotalAmount] = useState(0);

  useEffect(() => {
    calculateLoan();
  }, [loanAmount, tenure, interestRate]);

  const calculateLoan = () => {
    const principal = loanAmount;
    const ratePerMonth = interestRate / (12 * 100);
    const tenureMonths = tenure * 12;
    
    const emiAmount = (principal * ratePerMonth * Math.pow(1 + ratePerMonth, tenureMonths)) /
      (Math.pow(1 + ratePerMonth, tenureMonths) - 1);
    
    const totalPayment = emiAmount * tenureMonths;
    const totalInterestAmount = totalPayment - principal;

    setEmi(Math.round(emiAmount));
    setTotalInterest(Math.round(totalInterestAmount));
    setTotalAmount(Math.round(totalPayment));
  };

  return (
    <Box>
      <Paper
        sx={{
          bgcolor: 'primary.main',
          color: 'white',
          py: 6,
          mb: 4,
        }}
      >
        <Container maxWidth="lg">
          <Typography variant="h3" gutterBottom>
            Loan EMI Calculator
          </Typography>
          <Typography variant="h6">
            Plan your loan EMI and total payment
          </Typography>
        </Container>
      </Paper>

      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h5" gutterBottom>
                  Enter Loan Details
                </Typography>
                <Box sx={{ mt: 3 }}>
                  <Typography gutterBottom>Loan Amount (₹)</Typography>
                  <Slider
                    value={loanAmount}
                    onChange={(e, value) => setLoanAmount(value)}
                    min={100000}
                    max={15000000}
                    step={100000}
                    valueLabelDisplay="auto"
                    valueLabelFormat={(value) => `₹${value.toLocaleString()}`}
                  />
                  <TextField
                    fullWidth
                    value={loanAmount}
                    onChange={(e) => setLoanAmount(Number(e.target.value))}
                    type="number"
                    margin="normal"
                  />

                  <Typography gutterBottom sx={{ mt: 2 }}>Tenure (Years)</Typography>
                  <Slider
                    value={tenure}
                    onChange={(e, value) => setTenure(value)}
                    min={1}
                    max={30}
                    valueLabelDisplay="auto"
                  />
                  <TextField
                    fullWidth
                    value={tenure}
                    onChange={(e) => setTenure(Number(e.target.value))}
                    type="number"
                    margin="normal"
                  />

                  <Typography gutterBottom sx={{ mt: 2 }}>Interest Rate (%)</Typography>
                  <TextField
                    fullWidth
                    value={interestRate}
                    onChange={(e) => setInterestRate(Number(e.target.value))}
                    type="number"
                    margin="normal"
                  />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h5" gutterBottom>
                  Loan Summary
                </Typography>
                <Box sx={{ mt: 3 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Paper sx={{ p: 2, bgcolor: 'primary.main', color: 'white' }}>
                        <Typography variant="h6">Monthly EMI</Typography>
                        <Typography variant="h4">
                          ₹{emi.toLocaleString()}
                        </Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Paper sx={{ p: 2, bgcolor: 'grey.100' }}>
                        <Typography variant="subtitle1">Principal Amount</Typography>
                        <Typography variant="h6">
                          ₹{loanAmount.toLocaleString()}
                        </Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Paper sx={{ p: 2, bgcolor: 'grey.100' }}>
                        <Typography variant="subtitle1">Total Interest</Typography>
                        <Typography variant="h6">
                          ₹{totalInterest.toLocaleString()}
                        </Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={12}>
                      <Paper sx={{ p: 2, bgcolor: 'secondary.main', color: 'white' }}>
                        <Typography variant="subtitle1">Total Amount Payable</Typography>
                        <Typography variant="h5">
                          ₹{totalAmount.toLocaleString()}
                        </Typography>
                      </Paper>
                    </Grid>
                  </Grid>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}

export default Calculator; 